import {IconArrowBarToDown, IconDeviceGamepad2, IconFlame, IconHome, IconTicket, IconTrophy} from "@tabler/icons-react";
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {useAuthStore} from "@/store/useAuth.store";
import {useBalanceStore} from "@/store/useBalance.store";
import {SLOTS_GAMES_NAVIGATION} from "@/util/categoryDisplayName";
import styles from "./MobileBottomDock.module.css";

/**
 * @typedef {"slots" | "lottery" | "feed" | "sports" | "deposit"} DockItemId
 */

/** Route prefix matching for active detection */
const ROUTE_MAP = {
  slots: ["/casino", "/games", "/gameslist"],
  lottery: ["/lottery", "/lotoven"],
  feed: ["/game-feed"],
  sports: ["/sports"],
};

const HOME_REPLACEABLE_IDS = new Set(["slots", "lottery", "sports", "feed"]);

const ICON_SIZE = 22;
const CENTER_ICON_SIZE = 26;

const MobileBottomDock = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();

  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);

  const activeId = useMemo(() => {
    const path = location.pathname;
    for (const [id, prefixes] of Object.entries(ROUTE_MAP)) {
      if (prefixes.some(p => path === p || path.startsWith(`${p}/`))) {
        return id;
      }
    }
    return null;
  }, [location.pathname]);

  const requireAuth = useCallback(
    callback => {
      if (authStatus !== "authenticated") {
        useAuthStore.getState().handleAuthForm("login");
        handleShowAuthModal();
        return;
      }
      callback();
    },
    [authStatus, handleShowAuthModal]
  );

  const navigateTo = useCallback(
    (path, options) => {
      navigate(path, options);
      window.scrollTo({top: 0, behavior: "smooth"});
    },
    [navigate]
  );

  const handleHome = useCallback(() => {
    navigateTo("/");
  }, [navigateTo]);

  const handleSlots = useCallback(() => {
    requireAuth(() => navigateTo(SLOTS_GAMES_NAVIGATION.pathname));
  }, [requireAuth, navigateTo]);

  const handleLottery = useCallback(() => {
    requireAuth(() => navigateTo("/lottery"));
  }, [requireAuth, navigateTo]);

  const handleFeed = useCallback(() => {
    requireAuth(() => navigateTo("/game-feed"));
  }, [requireAuth, navigateTo]);

  const handleSports = useCallback(() => {
    requireAuth(() => navigateTo("/sports"));
  }, [requireAuth, navigateTo]);

  const handleDeposit = useCallback(() => {
    requireAuth(() => handleBankAccountModal());
  }, [requireAuth, handleBankAccountModal]);

  const sideItems = useMemo(
    () => [
      {
        id: "slots",
        label: t("floatingMenuSlots"),
        icon: IconDeviceGamepad2,
        onClick: handleSlots,
      },
      {
        id: "lottery",
        label: t("floatingMenuLottery"),
        icon: IconTicket,
        onClick: handleLottery,
      },
      {
        id: "sports",
        label: t("floatingMenuSports"),
        icon: IconTrophy,
        onClick: handleSports,
      },
      {
        id: "feed",
        label: t("floatingMenuFeed"),
        icon: IconFlame,
        onClick: handleFeed,
      },
    ],
    [t, handleSlots, handleLottery, handleSports, handleFeed]
  );

  const resolveDockItem = useCallback(
    item => {
      if (activeId === item.id && HOME_REPLACEABLE_IDS.has(item.id)) {
        return {
          ...item,
          displayId: `${item.id}-home`,
          label: t("floatingMenuHome"),
          icon: IconHome,
          onClick: handleHome,
        };
      }
      return {...item, displayId: item.id};
    },
    [activeId, t, handleHome]
  );

  const renderSideItem = useCallback(
    (item, extraProps = {}) => {
      const resolved = resolveDockItem(item);
      const Icon = resolved.icon;

      return (
        <button
          key={resolved.displayId}
          type="button"
          className={styles.dockItem}
          onClick={resolved.onClick}
          aria-label={resolved.label}
          {...extraProps}
        >
          <span className={styles.dockIndicator} />
          <span className={styles.dockIcon}>
            <Icon
              size={ICON_SIZE}
              stroke={1.8}
            />
          </span>
          <span className={styles.dockLabel}>{resolved.label}</span>
        </button>
      );
    },
    [resolveDockItem]
  );

  return (
    <nav
      className={`${styles.dock} bottomNavMenu`}
      aria-label="Main navigation"
    >
      <div className={styles.dockRow}>
        {/* Left two items */}
        {sideItems.slice(0, 2).map(item => renderSideItem(item))}

        {/* Center — Deposit (prominent) */}
        <button
          type="button"
          className={styles.dockCenter}
          onClick={handleDeposit}
          aria-label={t("floatBtnDeposit")}
        >
          <span className={styles.centerRing} />
          <span className={styles.centerPulse} />
          <span className={styles.centerBtn}>
            <IconArrowBarToDown
              size={CENTER_ICON_SIZE}
              stroke={2}
            />
          </span>
          <span className={styles.centerLabel}>{t("floatBtnDeposit")}</span>
        </button>

        {/* Right two items */}
        {sideItems.slice(2).map(item => renderSideItem(item))}
      </div>
    </nav>
  );
};

export default MobileBottomDock;
